import {
  ShieldCheck,
  Users,
  GraduationCap,
  Building2,
  Zap,
  MonitorPlay,
} from 'lucide-react'

export type Course = {
  id: string
  title: string
  level?: string
  category: 'HACCP' | 'Food Safety' | 'Allergens' | 'Management'
  price: string
  duration: string
  delivery: string[]
  description: string
  shortDescription: string
  modules: string[]
  certification: string
  popular?: boolean
}

export const courses: Course[] = [
  {
    id: 'haccp-level-1-2',
    title: 'HACCP Level 1 & 2',
    level: 'Level 1 & 2',
    category: 'HACCP',
    price: '€39',
    duration: '~120 Minutes',
    delivery: ['Online'],
    shortDescription:
      'Complete food safety training combining induction-level skills and HACCP principles. Get certified instantly.',
    description:
      'This HACCP Level 1 & 2 training course provides complete food safety training covering both induction-level food safety skills and the additional HACCP knowledge expected of employees working with food.',
    modules: [
      'Introduction',
      'Level 1 - Stage 1',
      'Level 1 - Stage 2',
      'Level 2',
      'Level 2 Exam',
    ],
    certification: 'CPD Accredited — 4 Credits',
    popular: true,
  },
  {
    id: 'haccp-level-1',
    title: 'HACCP Level 1',
    level: 'Level 1',
    category: 'HACCP',
    price: '€25',
    duration: '1 Hour',
    delivery: ['Online'],
    shortDescription:
      'Essential induction-level food safety training for new food handlers. Start and finish today.',
    description:
      'The HACCP Level 1 course provides the fundamental skills required for all food handlers. Covers the basics of food safety, personal hygiene, and cross-contamination prevention. Complete online at your own pace.',
    modules: [
      'Introduction to Food Safety',
      'Microbiological Hazards',
      'Food Contamination',
      'Personal Hygiene',
      'Cleaning and Disinfection',
    ],
    certification: 'Certified by FSAI Guidelines',
    popular: true,
  },
  {
    id: 'haccp-level-2',
    title: 'HACCP Level 2',
    level: 'Level 2',
    category: 'HACCP',
    price: '€35',
    duration: '2 Hours',
    delivery: ['Online'],
    shortDescription:
      'Comprehensive HACCP training for experienced food handlers, chefs, and supervisors.',
    description:
      'HACCP Level 2 is designed for food handlers, chefs, and supervisors who need a deeper understanding of food safety management systems. This online course ensures compliance with Regulation (EC) No 852/2004.',
    modules: [
      'Legal Responsibilities',
      'HACCP Principles',
      'Hazard Analysis',
      'Monitoring & Corrective Actions',
      'Pest Control',
      'Record Keeping',
    ],
    certification: 'Certified by FSAI Guidelines',
    popular: true,
  },
  {
    id: 'haccp-level-3',
    title: 'HACCP Level 3',
    level: 'Level 3',
    category: 'HACCP',
    price: '€99',
    duration: '6 Hours',
    delivery: ['Online'],
    shortDescription:
      'Advanced management-level HACCP training for head chefs and food safety managers.',
    description:
      'Management level training for those responsible for designing and managing the HACCP system. This comprehensive online course covers audit preparation, team management, and advanced food safety culture.',
    modules: [
      'Design & Implementation of HACCP',
      'Validation & Verification',
      'Food Safety Culture',
      'Staff Training Management',
      'Audit Preparation (EHO)',
    ],
    certification: 'QQI Level 5 Equivalent',
  },
  {
    id: 'allergen-awareness',
    title: 'Allergen Awareness',
    category: 'Allergens',
    price: '€29',
    duration: '1.5 Hours',
    delivery: ['Online'],
    shortDescription:
      'Essential online training on the 14 major allergens for all food service staff.',
    description:
      'Mandatory training for all food service staff to understand the 14 major allergens, how to handle allergen requests, and preventing cross-contact in the kitchen. Complete fully online.',
    modules: [
      'The 14 Major Allergens',
      'Communication with Customers',
      'Kitchen Protocols',
      'Emergency Procedures (Anaphylaxis)',
    ],
    certification: 'Certificate of Completion',
  },
  {
    id: 'food-safety-management',
    title: 'Food Safety Management',
    category: 'Management',
    price: '€149',
    duration: '8 Hours',
    delivery: ['Online'],
    shortDescription:
      'Comprehensive online food safety management training for supervisors and managers.',
    description:
      'A thorough online course covering food safety management systems, compliance frameworks, and team training strategies. Ideal for supervisors and managers responsible for food safety in their organisation.',
    modules: [
      'Food Safety Management Systems',
      'Risk Assessment & HACCP',
      'Staff Training & Supervision',
      'Compliance & Documentation',
    ],
    certification: 'CPD Accredited',
  },
]

export const features = [
  {
    title: 'Learn Anywhere',
    description:
      'Complete your training on any device — desktop, tablet, or mobile. Your progress syncs automatically.',
    icon: MonitorPlay,
  },
  {
    title: 'FSAI Compliant',
    description:
      'All courses are designed to meet Food Safety Authority of Ireland standards and guidelines.',
    icon: ShieldCheck,
  },
  {
    title: 'Team Management',
    description:
      'Enrol your entire team online with our group dashboard. Track progress and download certificates.',
    icon: Building2,
  },
  {
    title: 'Instant Certification',
    description:
      'Get your CPD-accredited certificate immediately upon passing. No waiting, no delays.',
    icon: Zap,
  },
]
